import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/API';
import { MappedField } from '@/types/study';
import { FormTreeData } from '@/features/submission_forms/types';

// The parent Study surfaced on a StudySubmission or StudySiteSubmission detail panel.
export interface SubmissionStudy {
  id: string;
  type: 'Study';
  title: string;
  protocol_number?: string;
  protocol_title?: string;
  status?: string | null;
  sponsor_name?: string;
  cro_name?: string;
  can_view: boolean;
  mapped_fields: MappedField[];
  display_id: string | null;
  show_path: string;
}

// Immediate parent for navigation: Study for StudySubmission, StudySite for StudySiteSubmission.
export interface SubmissionOwner {
  id: string;
  type: 'Study' | 'StudySite';
  display_id: string | null;
  show_path: string;
  can_view: boolean;
}

// The parent StudySite surfaced on a StudySiteSubmission detail panel.
export interface SubmissionSite {
  id: string;
  display_id: string | null;
  site_name: string;
  status: string | null;
  pi_name: string | null;
  countries: string[];
  submitted_at: string | null;
  show_path: string;
  can_view: boolean;
  mapped_fields: MappedField[];
}

export interface Submission {
  id: string;
  display_id: string;
  title: string;
  status: string;
  sub_status?: string | null;
  sub_status_label?: string | null;
  protocol_title?: string;
  protocol_number?: string;
  site_name?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  last_saved?: string;
  // Submission is in a state that can be edited
  editable: boolean;
  // User has the required permissions to edit this submission
  can_edit?: boolean;
  show_path: string;
  study?: SubmissionStudy;
  user?: {
    id: string;
    cognito_sub: string;
  };
  submission_template?: {
    id: string;
    category: string;
    sub_category?: string;
    variant?: string;
  };
  submission_template_id?: string | null;
  question_tree?: FormTreeData[];
  can_manage_users?: boolean;
  // Whether the viewer may send invitations from this page (SubmissionPolicy#invite_users?).
  // Narrower than can_manage_users: IRB Ops can see the Users tab but not invite.
  can_invite_users?: boolean;
  has_finalized_zip?: boolean;
  site?: SubmissionSite;
  submission_role?:
    | 'submission_manager'
    | 'submission_viewer'
    | 'irb_non_affiliated_reviewer';
  submission_type?: 'Study' | 'StudySite' | 'Submission';
  // A study-less filing (GEN/Advisory/NHSR/Invoice) owned by a synthetic
  // wrapper study, or a genuinely ownerless legacy row: there is no real
  // Study/StudySite to surface, so no detail panel is shown.
  standalone?: boolean;
  owner?: SubmissionOwner;
  // Whose users this submission page's Users tab lists (`path`) — the owning
  // StudySite for the "New Site/Investigator" submission that founded it, when
  // the viewer can invite users on that site (or is IRB Ops); otherwise the
  // submission itself — and whether the viewer may edit that list
  // (`manageable`). Resolved by SubmissionUserPolicy#users_tab.
  users_tab?: {
    path: string;
    manageable: boolean;
  };
}

interface UseSubmissionReturn {
  submission: Submission | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  clearError: () => void;
}

const fetchSubmission = async (submissionId: string): Promise<Submission> => {
  const response = await API.authenticated.fetch(
    `/submissions/${submissionId}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch submission');
  }

  return response.json();
};

export const useSubmission = (
  submissionId: string | null
): UseSubmissionReturn => {
  const {
    data: submission = null,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['submission', submissionId],
    queryFn: () => fetchSubmission(submissionId!),
    enabled: !!submissionId,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  return {
    submission,
    isLoading,
    error: error ? (error as Error).message : null,
    refetch,
    clearError: () => {}, // Not needed with React Query
  };
};
